// k:env-boundary — resolves the branding profile from the environment as a fallback (BE-005).
import { homedir } from 'os';
import { join } from 'path';
import type { BrandConfig } from './types';

/**
 * Expand a leading `~` in a path against the calling user's home directory.
 * Returns the path unchanged if it does not start with `~`.
 */
export function expandHome(p: string): string {
  if (p === '~') return homedir();
  if (p.startsWith('~/')) return join(homedir(), p.slice(2));
  return p;
}

/**
 * The profile used when the user names none. A subscriber that only ever
 * talks to one backend never has to think about profiles: everything lands
 * at the brand's base config dir, exactly as it did before profiles existed.
 */
export const DEFAULT_PROFILE = 'default';

/**
 * Resolve the active profile for a command.
 *
 * Profiles isolate one pairing (credentials, identity, pools, spend, audit)
 * from another so the same machine can be paired to, say, a local backend
 * and a stage backend without the two clobbering each other's config. The
 * name is taken from an explicit `--profile` flag, then the brand-specific
 * `<PRODUCT>_PROFILE` env var (e.g. `SIFTER_PROFILE`), then the default.
 *
 * Names are constrained to a filesystem- and shell-safe slug so a profile
 * can never collide with the base dir's own files (`config.json`,
 * `sifter.yaml`, ...), which all contain a dot.
 */
export function resolveProfile(brand: BrandConfig, explicit?: string): string {
  const envKey = `${brand.product.id.toUpperCase().replace(/[^A-Z0-9]/g, '_')}_PROFILE`;
  const candidate = explicit ?? process.env[envKey] ?? DEFAULT_PROFILE;
  if (!/^[a-z0-9][a-z0-9-]*$/.test(candidate)) {
    throw new Error(
      `Invalid profile "${candidate}": use lowercase letters, digits, and hyphens (e.g. "stage", "local").`,
    );
  }
  return candidate;
}

/**
 * Resolved filesystem paths a brand uses at runtime. All paths are absolute.
 */
export interface BrandedPaths {
  profile: string;
  configDir: string;
  configFile: string;        // device-code / paired-identity config
  auditFile: string;
  spendStateFile: string;
}

/**
 * Resolve the absolute paths for a profile. The default profile lives at
 * the brand's base config dir (so existing single-backend installs are
 * untouched); every named profile gets its own subdir under it, e.g.
 * `~/.sifter/stage/`.
 */
export function resolvePaths(
  brand: BrandConfig,
  profile: string = DEFAULT_PROFILE,
): BrandedPaths {
  const base = expandHome(brand.paths.configDir);
  const configDir = profile === DEFAULT_PROFILE ? base : join(base, profile);
  const auditFile = brand.audit?.path
    ? expandHome(brand.audit.path)
    : join(configDir, 'audit.jsonl');

  return {
    profile,
    configDir,
    configFile: join(configDir, 'config.json'),
    auditFile,
    spendStateFile: join(configDir, 'spend-state.json'),
  };
}

/**
 * Where `run`/`validate` look for the runtime YAML. The default profile
 * keeps the brand's configured search list (which may include a shared
 * cross-machine path); a named profile reads only its own dir, so a missing
 * profile config fails loudly instead of silently falling back to another
 * profile's file.
 */
export function runConfigSearchPaths(brand: BrandConfig, profile: string): string[] {
  if (profile === DEFAULT_PROFILE) {
    return brand.paths.configSearchPaths ? [...brand.paths.configSearchPaths] : [];
  }
  return [join(resolvePaths(brand, profile).configDir, `${brand.product.id}.yaml`)];
}

/**
 * Substitute `{user}` and `{hostname}` into a template string.
 */
export function renderTemplate(
  template: string,
  vars: Record<string, string>,
): string {
  return template.replace(/\{(\w+)\}/g, (_match, key) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : `{${key}}`,
  );
}
